import Multiselect from 'multiselect-react-dropdown';
import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import { BiPlusCircle, BiTrash } from "react-icons/bi";
import { toast } from "react-toastify";
import { Button } from '../../components/ui';
import { FormLogicAction, FormLogicOperation, LogicOperations } from '../../core/constants/common';
import FormEditService from "../../core/services/FromEditService";
import QuestionFooterActions from "../Question/QuestionFooterActions";
import '../../components/ui/ui.scss';

const QuestionLogicForm = ({ formData, nextQuestionCallback, cancelCallback, saveCallback }) => {

	const defaultRule = {
		id: 0,
		operation: FormLogicOperation.EQUAL,
		value: null,
		option: { id: 0 }
	};

	const defaultRules = [{
		id: 0,
		operation: FormLogicOperation.EQUAL,
		value: null,
		option: { id: 0 }
	}];

	const defaultDisplayField = {
		id: 0,
		fields: [],
		subSectionId: 0,
		sectionId: 0
	};

	const defaultDisplayFields = [{
		id: 0,
		fields: [],
		subSectionId: 0,
		sectionId: 0
	}];

	const [renderedOn, setRenderedOn] = useState(0);
	const [logicDefinitions, setLogicDefinitions] = useState([]);

	useEffect(() => {
		fetchLogics(formData.id, formData.fieldId, formData.sectionId ?? 0);
	}, [formData.id, formData.fieldId, formData.sectionId]);

	const fetchLogics = (formId, fieldId, sectionId) => {
		FormEditService.getFormFieldLogics(formId, fieldId, sectionId)
			.then(response => {
				if (response?.data?.logicDefinitions?.length > 0) {
					const logics = response.data.logicDefinitions;
					logics.forEach(logic => {
						if (!logic.displayFields || logic.displayFields?.length === 0) {
							logic.displayFields = defaultDisplayFields;
						}
					});
					setLogicDefinitions(logics);
				} else {
					setLogicDefinitions([]);
				}
			}).catch(error => {
				toast.error(error.message, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
	};

	const addRule = (logicIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].rules.push(defaultRule);
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const removeRule = (logicIndex, ruleIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].rules.splice(ruleIndex, 1);
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const addLogic = () => {
		const defs = logicDefinitions;
		defs.push({
			action: FormLogicAction.SHOW,
			rules: defaultRules,
			displayFields: defaultDisplayFields
		});
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const addField = (logicIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].displayFields.push(defaultDisplayField);
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const removeField = (logicIndex, fieldIndex) => {
		const defs = logicDefinitions;
		if (defs[logicIndex].displayFields.length === 1) {
			return;
		}
		defs[logicIndex].displayFields.splice(fieldIndex, 1);
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const setRuleOperation = (value, logicIndex, ruleIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].rules[ruleIndex].operation = value;
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const setRuleOption = (value, logicIndex, ruleIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].rules[ruleIndex].option.id = +value;
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const setFieldSection = (value, logicIndex, fieldIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].displayFields[fieldIndex].sectionId = +value;
		defs[logicIndex].displayFields[fieldIndex].subSectionId = 0;
		defs[logicIndex].displayFields[fieldIndex].fields = [];
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const setFieldSubSection = (value, logicIndex, fieldIndex) => {
		const defs = logicDefinitions;
		defs[logicIndex].displayFields[fieldIndex].subSectionId = +value;
		defs[logicIndex].displayFields[fieldIndex].fields = [];
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const pushFieldQuestion = (logicIndex, fieldIndex, field) => {
		const defs = logicDefinitions;
		defs[logicIndex].displayFields[fieldIndex].fields.push({ id: field.id, name: field.name });
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const removeFieldQuestion = (logicIndex, fieldIndex, index) => {
		const defs = logicDefinitions;
		defs[logicIndex].displayFields[fieldIndex].fields.splice(index, 1);
		setLogicDefinitions(defs);
		setRenderedOn(Date.now());
	};

	const removeLogic = (logicIndex) => {
		const defs = JSON.parse(JSON.stringify(logicDefinitions));
		const logicToDelete = defs[logicIndex];
		if (logicToDelete.id > 0) {
			FormEditService.deleteDisplayLogic(
				formData.id,
				formData.fieldId,
				logicToDelete.id
			).then(res => {
				if (res.status !== 204) return;

				fetchLogics(formData.id, formData.fieldId, formData.sectionId ?? 0);
			}).catch(error => {
				toast.error(error.message, {
					position: toast.POSITION.TOP_RIGHT,
				});
			});
		} else {
			defs.splice(logicIndex, 1);
			setLogicDefinitions(defs);
			setRenderedOn(Date.now());
		}
	};

	const onSave = () => {
		const logicData = [];
		logicDefinitions.forEach(logic => {
			logicData.push({
				id: logic.id,
				action: logic.action,
				rules: logic.rules,
				displayFields: logic.displayFields
			});
		});
		FormEditService.saveFormFieldLogics(
			formData.id,
			formData.fieldId,
			formData.sectionId ?? 0,
			{ logicDefinitions: logicData }
		).then(response => {
			if (response.status === 200 || response.status === 201) {
				toast.success('Success', {
					position: toast.POSITION.TOP_RIGHT,
				});
				cancelCallback();
			}
		}).catch(error => {
			toast.error(error.message, {
				position: toast.POSITION.TOP_RIGHT,
			});
		});
	};

	const getQuestionsList = (logicIndex, fieldIndex, displayField) => {
		let show = false;
		let questions = [];
		if (displayField.subSectionId > 0) {
			let subSections = [];
			if (displayField.sectionId > 0) {
				subSections = formData.sections?.find(sec => +sec.id === displayField.sectionId)?.subSections ?? [];
			} else {
				subSections = formData.sections?.find(sec => +sec.id === +formData.sectionId)?.subSections ?? [];
			}
			const subSection = subSections?.find(sec => sec.id === displayField.subSectionId);
			show = subSection ? true : false;
			questions = subSection?.fields?.filter(q => +q.id !== +formData.fieldId);
		} else if (displayField.sectionId > 0) {
			const section = formData.sections?.find(sec => +sec.id === displayField.sectionId);
			show = section ? true : false;
			questions = section?.fields?.filter(q => +q.id !== +formData.fieldId);
		} else {
			show = true;
			questions = formData.questions?.filter(q => +q.id !== +formData.fieldId);
		}
		return (show &&
			<div className="builder-logic-multiselect">
				<Multiselect
					showCheckbox="true"
					options={questions}
					placeholder={`${getSelectedQuestionsCount(displayField.fields)} questions chosen`}
					selectedValues={getSelectedValues(displayField.fields, questions)}
					hideSelectedList="true"
					onSelect={(selectedList, selectedItem) => {
						onSelectDeSelectML(displayField.fields, selectedItem, logicIndex, fieldIndex);
					}}
					onRemove={(selectedList, selectedItem) => {
						onSelectDeSelectML(displayField.fields, selectedItem, logicIndex, fieldIndex);
					}}
					displayValue="name"
				/>
			</div>
		);
	};

	const getSelectedValues = (fields, questions) => {
		const data = [];
		fields.forEach(f => {
			data.push({ id: +f.id, name: questions.find(q => +q.id === f.id)?.name ?? null });
		});
		return data;
	};

	const getSelectedQuestionsCount = (selectedFields) => {
		return selectedFields.filter(f => f.id > 0)?.length ?? 0;
	};

	const onSelectDeSelectML = (fields, selectedItem, logicIndex, fieldIndex) => {
		let duplicateIndex = fields?.findIndex(f => f.id === selectedItem.id);
		if (duplicateIndex >= 0) {
			removeFieldQuestion(logicIndex, fieldIndex, duplicateIndex);
		} else {
			pushFieldQuestion(logicIndex, fieldIndex, selectedItem);
		}
	};

	const getRuleOperationOptions = (ruleIndex) => {
		return LogicOperations.filter((operation) => {
			if (ruleIndex === 0) {
				return operation.id !== FormLogicOperation.OR && operation.id !== FormLogicOperation.AND;
			}
			return true;
		});
	};

	const renderSectionSelector = (field, logicIndex, fieldIndex) => {
		return (
			<select
				className="builder-ui-select"
				value={field.sectionId}
				onChange={(e) => setFieldSection(e.target.value, logicIndex, fieldIndex)}>
				<option value="0">Select section</option>
				{formData.sections?.map(sec => (
					<option key={sec.id} value={+sec.id}>{sec.title}</option>
				))}
			</select>
		);
	};

	const renderSubSectionSelector = (field, logicIndex, fieldIndex) => {
		let subSectionsToIterate = [];
		if (+field.sectionId > 0) {
			subSectionsToIterate = formData.sections?.filter(s => +s.id === +field.sectionId);
		} else {
			subSectionsToIterate = formData.sections?.filter(s => +s.id === +formData.sectionId);
		}
		return (
			<select
				className="builder-ui-select"
				value={field.subSectionId}
				onChange={(e) => setFieldSubSection(e.target.value, logicIndex, fieldIndex)}>
				<option value="0">Select subsection</option>
				{subSectionsToIterate?.map((sec, secIndex) => (
					<React.Fragment key={secIndex}>
						{sec.subSections?.map(subSection => (
							<option key={subSection.id} value={+subSection.id}>{subSection.title}</option>
						))}
					</React.Fragment>
				))}
			</select>
		);
	};

	return (
		<>
			<div className='PS_Qus_Card_body'>
				<h6>Smart Rules</h6>
				{logicDefinitions.length == 0 &&
					<Alert key="infoAlert" variant="info">
						No smart rules available
					</Alert>}
				<Form className="mt-3">
					{
						logicDefinitions.map((logic, logicIndex) => {
							return (
								<div
									id={"logicForm-" + (logicIndex + 1)}
									key={logicIndex + 1}
									className="builder-logic-card">
									<div className="builder-logic-card__header">
										<span className="builder-logic-card__title">Smart rule {logicIndex + 1}</span>
										<Button
											variant="ghost"
											icon={<BiTrash />}
											aria-label="Remove smart rule"
											onClick={() => removeLogic(logicIndex)}
										/>
									</div>

									<div className="builder-logic-card__body">
										<div className="builder-logic-section">
											<h6 className="builder-logic-section__label">When</h6>
											{logic.rules.map((rule, ruleIndex) => (
												<div
													className={`builder-logic-rule${ruleIndex > 0 ? ' builder-logic-rule--continued' : ''}`}
													key={ruleIndex}
													id={"ruleControl-" + (ruleIndex + 1)}>
													{ruleIndex === 0 && (
														<span className="builder-logic-rule__prefix">If</span>
													)}
													<select
														className="builder-ui-select"
														value={rule.operation}
														onChange={(e) => setRuleOperation(e.target.value, logicIndex, ruleIndex)}>
														<option value="0">Select condition</option>
														{getRuleOperationOptions(ruleIndex).map((operation) => (
															<option key={operation.id} value={operation.id}>
																{ruleIndex === 0 ? `Option selected is ${operation.text}` : operation.text}
															</option>
														))}
													</select>
													<select
														className="builder-ui-select"
														value={rule?.option?.id}
														onChange={(e) => setRuleOption(e.target.value, logicIndex, ruleIndex)}>
														<option value="0">Select option</option>
														{formData.options?.map(option => (
															<option key={option.id} value={+option.id}>{option.name}</option>
														))}
													</select>
													<div className="builder-logic-rule__actions">
														{ruleIndex === logic.rules.length - 1 && (
															<>
																<Button
																	variant="ghost"
																	icon={<BiPlusCircle />}
																	aria-label="Add condition"
																	onClick={() => addRule(logicIndex)}
																/>
																{ruleIndex > 0 && (
																	<Button
																		variant="ghost"
																		icon={<BiTrash />}
																		aria-label="Remove condition"
																		onClick={() => removeRule(logicIndex, ruleIndex)}
																	/>
																)}
															</>
														)}
													</div>
												</div>
											))}
										</div>

										<div className="builder-logic-divider">
											<span>Then show</span>
										</div>

										<div className="builder-logic-section">
											<h6 className="builder-logic-section__label">Display fields</h6>
											{logic.displayFields?.map((field, fieldIndex) => (
												<div
													className="builder-logic-display"
													key={fieldIndex}
													id={"logicDisplayFieldKey-" + (fieldIndex + 1)}>
													{renderSectionSelector(field, logicIndex, fieldIndex)}
													{renderSubSectionSelector(field, logicIndex, fieldIndex)}
													{getQuestionsList(logicIndex, fieldIndex, field)}
													<div className="builder-logic-display__actions">
														<Button
															variant="ghost"
															icon={<BiPlusCircle />}
															aria-label="Add display field"
															onClick={() => addField(logicIndex)}
														/>
														<Button
															variant="ghost"
															icon={<BiTrash />}
															aria-label="Remove display field"
															onClick={() => removeField(logicIndex, fieldIndex)}
														/>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							);
						})
					}
				</Form>
			</div>
			<div className='PS_Qus_Card_footer'>
				<button className="btn btn-blank_outline " type='button'
					onClick={() => addLogic()}>
					<BiPlusCircle /> Add another smart rule
				</button>
			</div>
			<QuestionFooterActions
				nextQuestionCallback={null}
				cancelCallback={() => cancelCallback()}
				saveCallback={() => onSave()}>
			</QuestionFooterActions>
		</>

	);
};

export default QuestionLogicForm;
